/**
 * CSRF Token Management
 * Generates, stores, and manages CSRF tokens for form submissions
 *
 * Features:
 * - Generate cryptographically secure tokens
 * - Store tokens in memory with expiration
 * - Support for server-provided tokens
 * - Automatic refresh mechanism for long-form sessions
 * - Header injection for form submissions
 */

/**
 * CSRF Token structure
 */
export interface CsrfToken {
  /** Token value */
  token: string;
  /** Token generation timestamp */
  generatedAt: number;
  /** Token expiration timestamp */
  expiresAt: number;
  /** Whether token has been used */
  used: boolean;
}

/**
 * CSRF Token Manager configuration
 */
export interface CsrfTokenConfig {
  /** Token TTL in milliseconds (default: 1 hour) */
  ttl?: number;
  /** Token header name (default: X-CSRF-Token) */
  headerName?: string;
  /** Whether to store token in sessionStorage (default: false) */
  persistToStorage?: boolean;
  /** Storage key for persisted token (default: _csrf_token) */
  storageKey?: string;
}

/**
 * CSRF Token Manager - Singleton pattern
 * Manages CSRF token generation, storage, and validation
 */
class CsrfTokenManager {
  private static instance: CsrfTokenManager | null = null;
  private tokens: Map<string, CsrfToken> = new Map();
  private config: Required<CsrfTokenConfig>;
  private currentTokenId: string | null = null;

  private constructor(config: CsrfTokenConfig = {}) {
    this.config = {
      ttl: config.ttl || 60 * 60 * 1000, // 1 hour default
      headerName: config.headerName || 'X-CSRF-Token',
      persistToStorage: config.persistToStorage || false,
      storageKey: config.storageKey || '_csrf_token',
    };

    // Restore token from storage if available
    if (this.config.persistToStorage) {
      this.restoreFromStorage();
    }
  }

  /**
   * Get or create singleton instance
   */
  static getInstance(config?: CsrfTokenConfig): CsrfTokenManager {
    if (!CsrfTokenManager.instance) {
      CsrfTokenManager.instance = new CsrfTokenManager(config);
    }
    return CsrfTokenManager.instance;
  }

  /**
   * Generate a new CSRF token
   * Uses Web Crypto API for secure random generation
   */
  async generateToken(): Promise<string> {
    try {
      // Use Web Crypto API for secure random values
      const buffer = new Uint8Array(32);
      if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(buffer);
      } else {
        // Fallback: generate random values manually (less secure)
        for (let i = 0; i < buffer.length; i++) {
          buffer[i] = Math.floor(Math.random() * 256);
        }
      }

      // Convert to hex string
      const token = Array.from(buffer)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');

      // Create token metadata
      const tokenId = this.generateTokenId();
      const now = Date.now();
      const csrfToken: CsrfToken = {
        token,
        generatedAt: now,
        expiresAt: now + this.config.ttl,
        used: false,
      };

      // Store token
      this.tokens.set(tokenId, csrfToken);
      this.currentTokenId = tokenId;

      // Persist to storage if enabled
      if (this.config.persistToStorage) {
        this.persistToStorage(tokenId, csrfToken);
      }

      return token;
    } catch (error) {
      console.error('Failed to generate CSRF token:', error);
      throw new Error('CSRF token generation failed');
    }
  }

  /**
   * Get current CSRF token
   * Returns existing token if valid, generates new one if expired or missing
   */
  async getToken(): Promise<string> {
    if (this.currentTokenId && this.tokens.has(this.currentTokenId)) {
      const token = this.tokens.get(this.currentTokenId)!;
      if (!this.isTokenExpired(token)) {
        return token.token;
      }
    }

    // Generate new token if current one is expired or missing
    return this.generateToken();
  }

  /**
   * Validate a CSRF token
   * Checks expiration and usage status
   */
  validateToken(token: string, markAsUsed: boolean = true): boolean {
    // Search through all tokens to find matching one
    for (const [tokenId, csrfToken] of this.tokens) {
      if (csrfToken.token === token) {
        // Check if token is valid
        if (this.isTokenExpired(csrfToken)) {
          this.tokens.delete(tokenId);
          return false;
        }

        // Check if already used (optional strict mode)
        if (csrfToken.used) {
          return false;
        }

        // Mark as used if requested
        if (markAsUsed) {
          csrfToken.used = true;
        }

        return true;
      }
    }

    return false;
  }

  /**
   * Verify and consume a CSRF token
   * Validates token and marks as used in one operation
   */
  verifyAndConsume(token: string): boolean {
    return this.validateToken(token, true);
  }

  /**
   * Set server-provided CSRF token
   * Useful when server provides token in initial response
   */
  async setServerToken(token: string, expiresInMs?: number): Promise<void> {
    const tokenId = this.generateTokenId();
    const now = Date.now();
    const csrfToken: CsrfToken = {
      token,
      generatedAt: now,
      expiresAt: now + (expiresInMs || this.config.ttl),
      used: false,
    };

    this.tokens.set(tokenId, csrfToken);
    this.currentTokenId = tokenId;

    if (this.config.persistToStorage) {
      this.persistToStorage(tokenId, csrfToken);
    }
  }

  /**
   * Get CSRF token header name
   */
  getHeaderName(): string {
    return this.config.headerName;
  }

  /**
   * Get CSRF token as header object
   * Ready to use in fetch or axios headers
   */
  async getTokenHeader(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return {
      [this.config.headerName]: token,
    };
  }

  /**
   * Refresh token (invalidate current and generate new)
   * Useful for security-conscious long-form sessions
   */
  async refreshToken(): Promise<string> {
    // Clear all tokens
    this.tokens.clear();
    this.currentTokenId = null;

    // Clear storage
    if (this.config.persistToStorage) {
      try {
        sessionStorage.removeItem(this.config.storageKey);
      } catch {
        // Storage may not be available
      }
    }

    // Generate new token
    return this.generateToken();
  }

  /**
   * Clean up expired tokens
   * Call periodically to prevent memory leaks
   */
  cleanupExpiredTokens(): number {
    let removedCount = 0;
    for (const [tokenId, token] of this.tokens) {
      if (this.isTokenExpired(token)) {
        this.tokens.delete(tokenId);
        removedCount++;
      }
    }
    return removedCount;
  }

  /**
   * Reset manager (for testing)
   */
  reset(): void {
    this.tokens.clear();
    this.currentTokenId = null;
    if (this.config.persistToStorage) {
      try {
        sessionStorage.removeItem(this.config.storageKey);
      } catch {
        // Storage may not be available
      }
    }
  }

  /**
   * Internal: Check if token is expired
   */
  private isTokenExpired(token: CsrfToken): boolean {
    return Date.now() > token.expiresAt;
  }

  /**
   * Internal: Generate unique token ID
   */
  private generateTokenId(): string {
    return `csrf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Internal: Persist token to storage
   */
  private persistToStorage(tokenId: string, token: CsrfToken): void {
    try {
      const data = {
        id: tokenId,
        token: token.token,
        expiresAt: token.expiresAt,
      };
      sessionStorage.setItem(this.config.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to persist CSRF token to storage:', error);
    }
  }

  /**
   * Internal: Restore token from storage
   */
  private restoreFromStorage(): void {
    try {
      const data = sessionStorage.getItem(this.config.storageKey);
      if (data) {
        const parsed = JSON.parse(data);
        const token: CsrfToken = {
          token: parsed.token,
          generatedAt: Date.now(),
          expiresAt: parsed.expiresAt,
          used: false,
        };

        // Only restore if not expired
        if (!this.isTokenExpired(token)) {
          this.tokens.set(parsed.id, token);
          this.currentTokenId = parsed.id;
        } else {
          sessionStorage.removeItem(this.config.storageKey);
        }
      }
    } catch (error) {
      console.warn('Failed to restore CSRF token from storage:', error);
    }
  }
}

/**
 * Get or generate CSRF token (convenience function)
 */
export async function getCsrfToken(config?: CsrfTokenConfig): Promise<string> {
  const manager = CsrfTokenManager.getInstance(config);
  return manager.getToken();
}

/**
 * Get CSRF token header object (convenience function)
 */
export async function getCsrfTokenHeader(config?: CsrfTokenConfig): Promise<Record<string, string>> {
  const manager = CsrfTokenManager.getInstance(config);
  return manager.getTokenHeader();
}

/**
 * Validate CSRF token (convenience function)
 */
export function validateCsrfToken(token: string): boolean {
  const manager = CsrfTokenManager.getInstance();
  return manager.validateToken(token, false);
}

/**
 * Verify and consume CSRF token (convenience function)
 */
export function verifyCsrfToken(token: string): boolean {
  const manager = CsrfTokenManager.getInstance();
  return manager.verifyAndConsume(token);
}

/**
 * Refresh CSRF token (convenience function)
 */
export async function refreshCsrfToken(config?: CsrfTokenConfig): Promise<string> {
  const manager = CsrfTokenManager.getInstance(config);
  return manager.refreshToken();
}

/**
 * Set server-provided CSRF token (convenience function)
 */
export async function setServerCsrfToken(
  token: string,
  expiresInMs?: number,
  config?: CsrfTokenConfig
): Promise<void> {
  const manager = CsrfTokenManager.getInstance(config);
  return manager.setServerToken(token, expiresInMs);
}

/**
 * Get CSRF token header name (convenience function)
 */
export function getCsrfTokenHeaderName(): string {
  const manager = CsrfTokenManager.getInstance();
  return manager.getHeaderName();
}

export default CsrfTokenManager;
